import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/game/config';
import { getBackground, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { getActivePack } from '@/game/packs';
import { isHalloweenPack } from '@/game/starGlyphs';
import { buildHalloweenBackdrop } from '@/game/ui/halloweenArt';

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  if (bg === 'fable') return fableVariant();
  return bg;
}

/**
 * The scene behind the board: nothing but the chosen backdrop.
 *
 * It used to be part of BoardScene, which is restarted between boards (RoundScene.advanceBoard).
 * A restart tears the scene's display list down and rebuilds it, and while the new board has
 * anything to load Phaser holds it in LOADING and renders none of it — so the backdrop blinked
 * out to the flat #0b0d10 canvas clear colour for those frames. On a picture board that is four
 * fresh .webp fetches every board, which on the web read as a flash of the app background at the
 * end of every round.
 *
 * Launched with the round and stopped with it, never restarted, so the picture behind the game
 * holds still whatever the board is doing. Registered before Board in the scene list
 * (src/main.ts) — that order is what puts it underneath.
 */
export class BackdropScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Backdrop' });
  }

  preload(): void {
    // Backdrops load on demand here (not in the global Preload) so the menu appears as soon as the
    // app boots instead of waiting on an image it doesn't show. "space" is a procedural starfield
    // (no image); Phaser caches the texture, so every later round pays nothing.
    // The Halloween lesson has its own backdrop, drawn in code — the chosen one is not loaded.
    if (isHalloweenPack(getActivePack())) return;
    const bg = getBackground();
    if (bg === 'space') return;
    const asset = backgroundAsset(bg);
    if (!this.textures.exists(`bg_${asset}`)) {
      this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
    }
  }

  create(): void {
    // Only while the Halloween lesson is active: its night scene replaces whatever backdrop the
    // player picked, and every other lesson is untouched.
    if (isHalloweenPack(getActivePack())) {
      buildHalloweenBackdrop(this);
      return;
    }
    const bg = getBackground();
    if (bg === 'space') {
      new Starfield(this);
      return;
    }
    const asset = backgroundAsset(bg);
    const key = `bg_${asset}`;
    if (!this.textures.exists(key)) {
      console.warn(`[Backdrop] background image missing: /images/background_${asset}.jpg — falling back to a starfield.`);
      new Starfield(this);
      return;
    }
    const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
    img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
  }
}
